<template>
    <div class="time-line-mobile" :class="{ empty: timelineIsEmpty || timelineIsComplete }">
        <div class="programs-list">
            <div
                v-if="!timelineIsComplete"
                class="title-row"
                :class="{ empty: timelineIsEmpty }"
            >
                <h2 class="title">Трек развития</h2>
                <TimelineHeaderTooltip
                    v-if="getPageContent && getPageContent['timeline_tooltip']"
                    class="title-tip"
                    :html="getPageContent['timeline_tooltip']"
                />
            </div>
            <div v-if="!timelineIsComplete" class="tabs-row" :class="{ empty: timelineIsEmpty }">
                <StyleTabs
                    v-if="!timelineIsEmpty"
                    class="tabs"
                    :tabs="tabs"
                    :activeTab="activeTab"
                    small
                    @change="changeTabHandler"
                />
                <div v-else class="tabs-empty">
                    Построить трек
                </div>
                <TimelineHeaderTooltip
                    v-if="getPageContent && getPageContent['timeline_tooltip']"
                    class="tabs-tip"
                    :html="getPageContent['timeline_tooltip']"
                />
                <router-link
                    :to="{ name: 'profession-categories' }"
                    class="tabs-add-button"
                ></router-link>
            </div>

            <Empty v-if="timelineIsEmpty" />
            <Completed v-else-if="timelineIsComplete" />
            <div v-else class="content-row">
                <ProgramsMobile
                    :items="displayPrograms"
                    @on-select="$emit('select', $event)"
                    :style="{marginBottom: stickyHeight + 'px'}"
                />
            </div>
            <div ref="stickyBlock" class="stickyBlock" :class="{ fixed: isSticky }">
                <TimelineLegend v-if="!timelineIsComplete && !timelineIsEmpty" class="legend-row"/>
                <div class="progress-row">
                    <ProgressBar
                        v-if="getFirstDateOfTrack && getLastDateOfTrack && !timelineIsComplete && !timelineIsEmpty"
                        :start-date="getFirstDateOfTrack"
                        :end-date="getLastDateOfTrack"
                    />
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import TimelineHeaderTooltip from "./HeaderTooltip";
import TimelineLegend from './componentsMobile/Legend';
import StyleTabs from "../StyleTabs";
import ProgramsMobile from "./componentsMobile/ProgramsMobile";
import ProgressBar from "./componentsMobile/ProgressBar";
import Empty from "./componentsMobile/Empty";
import Completed from "./componentsMobile/Completed";
import { mapGetters } from 'vuex';

const accountGetters = [
    'getTimelinePrograms',
    'getFirstDateOfTrack',
    'getLastDateOfTrack',
    'getPageContent',
    'getProgramById',
    'getModuleById',
    'getCompetenceLevels',
    'getCompetences',
    'getSoftCompetences',
    'getHardCompetences',
];

export default {
    name: "TimelineMobile",

    components: {
        TimelineHeaderTooltip,
        TimelineLegend,
        StyleTabs,
        ProgramsMobile,
        ProgressBar,
        Empty,
        Completed,
    },

    mounted() {
        let stickyElement = this.$refs.stickyBlock;
        this.stickyHeight = stickyElement.clientHeight;
        this.stickyOffset = stickyElement.offsetTop + this.stickyHeight;
        this.updateStickyBlock();
        window.addEventListener('scroll', this.updateStickyBlock);
    },

    destroyed() {
        window.removeEventListener('scroll', this.updateStickyBlock);
    },

    data() {
        return {
            activeTab: 0,
            isSticky: false,
            stickyOffset: 0,
            stickyHeight: 0,
            tabs: [
                {
                    title: 'Все',
                    key: 'all',
                    filter: (items) => items,
                },
                {
                    title: 'Запланировано',
                    key: 'featured',
                    filter: (items) => {
                        return items.filter(item => item.status !== 'completed');
                    },
                },
                {
                    title: 'Пройдено',
                    key: 'completed',
                    filter: (items) => {
                        return items.filter(item => item.status === 'completed');
                    },
                },
            ],
            activeProgram: null,
            monthsOffset: 0,
            monthsToShow: 6,
            filters: {
                status: '',
            },
            selectedProgramId: null,
            selectedModuleId: null,
        };
    },

    computed: {
        ...mapGetters('accountTrack', accountGetters),

        displayPrograms() {
            let items = this.getTimelinePrograms.reduce((items, item) => {
                let blocks = [];

                let programFields = {
                    key: `p${item.id}`,
                    programId: item.id,
                    programName: item.name,
                    programType: item.programType,
                    moduleId: null,
                    moduleName: null,
                    moment: {
                        start: item.moment.start.clone(),
                        end: item.moment.end.clone(),
                        duration: item.moment.duration.clone(),
                    },
                    hasUpdates: item.hasUpdates,
                    status: item.status,
                    size: item.size,
                    offset: item.offset,
                    educationType: item.educationType,
                };

                if (item.modules.length === 0) {
                    items.push(programFields);
                } else {
                    blocks = item.modules.map(module => {
                        return {
                            ...programFields,
                            key: `p${item.id}m${module.id}`,
                            moduleId: module.id,
                            moduleName: module.name,
                            moment: {
                                start: module.moment.start.clone(),
                                end: module.moment.end.clone(),
                                duration: module.moment.duration.clone(),
                            },
                            hasUpdates: module.hasUpdates,
                            status: module.status,
                            size: module.size,
                            offset: module.offset,
                        };
                    });
                    items = [...items, ...blocks];
                }

                return items;
            }, []);
            let sorted = this.getSortedByDateEnd(items);
            sorted = this.getSortedByIsIndicated(sorted);
            const filter = this.tabs[this.activeTab].filter;
            if (filter) {
                return filter(sorted);
            }
            return sorted;
        },

        timelineIsEmpty() {
            return !this.getTimelinePrograms.length;
        },

        timelineIsComplete() {
            if (this.getCompetences.length === 0) {
                return false;
            }
            for (const competence of this.getSoftCompetences) {
                if (!this.isLevelReached(competence.requiredLevel, competence.competenceLevel)) {
                    return false;
                }
            }
            for (const competence of this.getHardCompetences) {
                if (!competence.userAcknowledgment) {
                    return false;
                }
            }
            return !this.timelineIsEmpty && true;
        },
    },

    methods: {
        updateStickyBlock() {
            if (window.pageYOffset + window.innerHeight < this.stickyOffset) {
                this.isSticky = true;
            } else {
                this.isSticky = false;
            }
        },

        isLevelReached(required, current) {
            const competenceLevelsPlain = {};
            for (const [index, level] of this.getCompetenceLevels.entries()) {
                competenceLevelsPlain[level.key] = index;
            }
            return current && required && competenceLevelsPlain[current] <= competenceLevelsPlain[required];
        },

        closeProgramHandler() {
            this.selectedProgramId = null;
            this.selectedModuleId = null;
        },

        selectProgramHandler(program) {
            this.selectedProgramId = program.programId;
            this.selectedModuleId = program.moduleId;
        },

        changeTabHandler(tab) {
            this.activeTab = tab;
        },

        getSortedByDateEnd(items) {
            return [...items].sort((a, b) => {
                const first = a.moment ? a.moment.end : null;
                const second = b.moment ? b.moment.end : null;
                if (!first || !second || first.isSame(second)) {
                    return 0;
                } else if (first.isAfter(second)) {
                    return 1;
                }
                return -1;
            });
        },

        getSortedByIsIndicated(items) {
            return [...items].sort((a, b) => {
                if ((a.hasUpdates && b.hasUpdates) || (!a.hasUpdates && !b.hasUpdates)) {
                    return 0;
                } else if (!a.hasUpdates && b.hasUpdates) {
                    return 1;
                }
                return -1;
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/rem";

.time-line-mobile {
    min-height: 80vh;
    background-color: #FFFFFF;
    border: 1px solid #D2D5D9;

    &.empty {
        background-color: #F5F9FC;
    }

    @include on-xs {
        background-color: transparent;
        border: none;
    }

    .programs {
        &-list {
            display: flex;
            flex-flow: column nowrap;
            min-height: 80vh;
            padding-bottom: 24px;
            position: relative;

            @include on-xs {
                padding-bottom: 0;
            }
        }
    }

    .title {
        margin: 0;
        font-weight: 600;
        font-size: 22px;
        line-height: 30px;
        color: #2A2626;

        &-row {
            display: flex;
            flex-flow: row nowrap;
            justify-content: flex-start;
            align-items: center;
            margin-bottom: 8px;
            padding: 24px;

            &.empty {
                background-color: #ffffff;
            }

            @include on-xs {
                display: none;
                padding: 0;
            }
        }

        &-tip {
            margin-left: 10px;
        }
    }

    .tabs {
        margin: 0 auto 0 0;

        &-row {
            display: flex;
            flex-flow: row nowrap;
            justify-content: flex-start;
            align-items: center;
            margin-bottom: 32px;
            padding: 0 24px;

            &.empty {
                @include on-sm {
                    display: none;
                }

                @include on-xs {
                    display: flex;
                }
            }

            @include on-xs {
                margin-bottom: 16px;
                padding: 12px 16px;
                background-color: #FFFFFF;
            }
        }

        &-empty {
            margin: 0 auto 0 0;
            font-weight: 600;
            font-size: 16px;
            line-height: 24px;
            color: #2A2626;
        }

        &-tip {
            display: none;
            flex: 0 0 auto;

            @include on-xs {
                display: block;
            }
        }

        &-add-button {
            flex: 0 0 auto;
            width: 24px;
            height: 24px;
            margin: 0 0 0 16px;
            border-radius: 50%;
            background: #4663AB url("~@/assets/images/icons/plus-full-white.svg") no-repeat center/14px;
            display: none;

            @include on-xs {
                display: block;
            }
        }
    }

    .content-row {
        margin-bottom: 24px;
        padding: 0 24px;

        @include on-xs {
            padding: 0 16px;
        }
    }

    .legend-row {
        margin: auto 0 8px 0;
        padding: 0 24px;

        @include on-xs {
            margin-bottom: 0;
        }
    }
    @include on-xs {
        .stickyBlock{
            position: absolute;
            bottom: 0;
            box-shadow: 0px -2px 4px rgba(155, 161, 166, 0.15);
        }
        .fixed{
            position: fixed;
            bottom: 0;
            left: 0;
        }
    }

    .progress-row {
        padding: 0 24px;

        @include on-xs {
            padding: 0;
        }
    }
}
</style>